import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BedDouble, CheckCircle2, Clock, Cpu, KeyRound, RefreshCw, ShieldAlert } from "lucide-react";

const TONO = {
  CONFIRMADO_OPERATIVO: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDIENTE_DE_CONFIRMACION_FISICA: "bg-indigo-50 text-indigo-700 border-indigo-200",
  PREPARADO_EN_SOFTWARE: "bg-amber-50 text-amber-800 border-amber-200",
};
const ICONO = {
  CONFIRMADO_OPERATIVO: CheckCircle2,
  PENDIENTE_DE_CONFIRMACION_FISICA: Clock,
  PREPARADO_EN_SOFTWARE: ShieldAlert,
};

const Estado = ({ estado, label, testId }) => {
  const Icon = ICONO[estado] || ShieldAlert;
  return (
    <span data-testid={testId} data-estado={estado}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${TONO[estado] || TONO.PREPARADO_EN_SOFTWARE}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
};

const Dim = ({ ok, children, testId }) => (
  <span data-testid={testId} data-ok={ok ? "1" : "0"}
    className={`text-[11px] rounded-md px-1.5 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
    {children}
  </span>
);

const DispositivoFila = ({ d }) => (
  <div data-testid={`comm-device-${d.id}`} className="rounded-xl border border-gray-100 bg-slate-50/60 p-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-[#012472]">
        <Cpu className="h-4 w-4 text-slate-400" /> {d.rol}
        <span className="font-normal text-slate-500">· {d.rol_funcion}</span>
      </p>
      <Estado estado={d.estado_commissioning} label={d.estado_label} testId={`comm-device-estado-${d.id}`} />
    </div>
    <p className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
      <span>ID evento: <span className="font-mono text-slate-600">{d.identidad.identificador_evento || "no capturado"}</span></span>
      <span aria-hidden>·</span>
      <span>S/N: <span className="font-mono text-slate-600">{d.identidad.numero_serie || "no capturado"}</span></span>
      <span aria-hidden>·</span>
      <span>{d.rol === "HM" ? `MAC: ${d.identidad.mac_address || "—"}` : `Perfil: ${d.identidad.profile_id || "sin asignar"}`}</span>
      <span aria-hidden>·</span>
      <span>Tipo registrado: <span className="text-slate-600">{d.tipo || "sin capturar"}</span></span>
    </p>
    <p className="mt-1 text-[11px] text-slate-500">
      Relaciones: zona <span className="text-slate-600">{d.relaciones.zona_nombre || "sin zona"}</span> · cama{" "}
      <span className="text-slate-600">{d.relaciones.cama_etiqueta || "sin cama"}</span> · residentes{" "}
      {d.relaciones.personas_monitoreadas.length}
    </p>
    <p className="mt-1 text-[11px] text-slate-500">
      Transporte: <span className="text-slate-600">{d.transporte.detalle}</span>
    </p>
    <p data-testid={`comm-device-c2-${d.id}`} className="mt-1 text-[11px] text-slate-500">
      C2 (fuente autoritativa): <span className="font-medium text-slate-700">{d.c2.estado}</span> — {d.c2.motivo}
    </p>
    <p data-testid={`comm-device-next-${d.id}`} className="mt-1.5 text-xs font-medium text-[#012472]">
      Siguiente: {d.siguiente_accion}
    </p>
    {d.relaciones.avisos.map((a) => (
      <p key={a} className="mt-1 text-[11px] text-amber-700">{a}</p>
    ))}
  </div>
);

export default function CommissioningPorCama({ sitioId }) {
  const [data, setData] = useState(null);
  const [creds, setCreds] = useState([]);
  const [nombreCred, setNombreCred] = useState("");
  const [tokenNuevo, setTokenNuevo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, k] = await Promise.all([
      api.get(`/sitios/${sitioId}/commissioning`),
      api.get(`/ekh/bridge-credentials?sitio_id=${sitioId}`).catch(() => ({ data: [] })),
    ]);
    setData(c.data);
    setCreds(k.data || []);
  }, [sitioId]);

  useEffect(() => { load(); }, [load]);

  const crearCred = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ekh/bridge-credentials", { sitio_id: sitioId, nombre: nombreCred });
      setTokenNuevo(r.data.token);
      setNombreCred("");
      toast.success("Credencial creada. Copia el secreto ahora: no se vuelve a mostrar.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la credencial.");
    } finally { setBusy(false); }
  };

  const revocar = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/ekh/bridge-credentials/${cid}/revoke`);
      toast.success("Credencial revocada.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revocar.");
    } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-slate-400">Cargando commissioning…</p>;
  const r = data.resumen;

  return (
    <div data-testid="commissioning-por-cama" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-[#012472]">Commissioning por cama</h3>
          <p className="text-xs text-slate-500">{data.nota}</p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="comm-resumen" className="text-xs text-slate-600">
            {r.camas} camas · {r.dispositivos} equipos · {r.confirmados_operativos} confirmados ·{" "}
            {r.pendientes_confirmacion_fisica} esperando prueba física · {r.preparados_en_software} en preparación
          </span>
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="comm-refrescar" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {data.camas.length === 0 && <p className="text-sm text-slate-400">Este sitio no tiene camas registradas.</p>}
        {data.camas.map((c) => (
          <div key={c.cama_id} data-testid={`comm-cama-${c.cama_id}`} className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-[#012472]">
                  <BedDouble className="h-4 w-4 text-slate-400" /> {c.cama_etiqueta || "(cama sin etiqueta)"}
                  <span className="font-normal text-slate-500">· {c.zona_nombre || "sin habitación"}</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Residente: <span className="text-slate-700">{c.residente_nombre || "sin vincular"}</span>
                </p>
              </div>
              <Estado estado={c.estado_commissioning} label={c.estado_label} testId={`comm-cama-estado-${c.cama_id}`} />
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              <Dim ok={c.dimensiones.relaciones_preparadas} testId={`comm-dim-rel-${c.cama_id}`}>Relaciones preparadas</Dim>
              <Dim ok={c.dimensiones.operacion_tecnica_confirmada} testId={`comm-dim-c2-${c.cama_id}`}>Operación técnica (C2)</Dim>
              <Dim ok={c.dimensiones.plan_respuesta_completo} testId={`comm-dim-plan-${c.cama_id}`}>Plan de respuesta</Dim>
            </div>

            <p data-testid={`comm-cama-next-${c.cama_id}`} className="mt-2 text-xs font-medium text-[#012472]">
              Siguiente paso: {c.siguiente_accion}
            </p>

            <div className="mt-3 grid gap-2 lg:grid-cols-3">
              {c.dispositivos.map((d) => <DispositivoFila key={d.id} d={d} />)}
              {c.roles_faltantes.map((rol) => (
                <div key={rol} data-testid={`comm-falta-${c.cama_id}-${rol}`}
                  className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-800">
                  Falta registrar el dispositivo <span className="font-semibold">{rol}</span> de esta cama.
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div data-testid="comm-transporte" className="rounded-2xl border border-gray-100 bg-white p-4">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-[#012472]">
          <KeyRound className="h-4 w-4 text-slate-400" /> Aprovisionamiento de transporte del sitio
        </h4>
        <p className="mt-1 text-xs text-slate-500">
          Puentes HM activos: {(data.transporte_sitio.puentes_hm || []).length} · Credenciales Bearer EKT/EKH activas:{" "}
          {(data.transporte_sitio.credenciales_bearer_activas || []).length}
        </p>

        {tokenNuevo && (
          <div data-testid="comm-cred-secreto" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-800">Secreto mostrado UNA sola vez. Cópialo ahora.</p>
            <p className="mt-1 break-all font-mono text-xs text-slate-700">{tokenNuevo}</p>
            <Button size="sm" variant="outline" className="mt-2 rounded-lg" data-testid="comm-cred-secreto-ocultar"
              onClick={() => setTokenNuevo(null)}>Ya lo copié</Button>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wide text-slate-400">Nombre de la credencial</label>
            <Input data-testid="comm-cred-nombre" className="rounded-xl" value={nombreCred}
              placeholder="Bridge EKT/EKH del centro" onChange={(e) => setNombreCred(e.target.value)} />
          </div>
          <Button data-testid="comm-cred-crear" disabled={busy} onClick={crearCred}
            className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white">Crear credencial Bearer</Button>
        </div>

        <div className="mt-3 divide-y divide-gray-100">
          {creds.length === 0 && <p className="text-xs text-slate-400">Sin credenciales emitidas para este sitio.</p>}
          {creds.filter((k) => k.estado === "active").map((k) => (
            <div key={k.id} data-testid={`comm-cred-${k.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <p className="text-xs text-slate-600">
                <span className="font-medium text-slate-700">{k.nombre || "(sin nombre)"}</span>{" "}
                <span className="font-mono">{k.token_prefix}…</span> · {k.estado}
                {k.last_used_at ? ` · último uso ${k.last_used_at}` : " · sin uso registrado"}
              </p>
              {k.estado === "active" && (
                <Button variant="ghost" size="sm" className="rounded-lg text-slate-500" disabled={busy}
                  data-testid={`comm-cred-revocar-${k.id}`} onClick={() => revocar(k.id)}>Revocar</Button>
              )}
            </div>
          ))}
          {creds.some((k) => k.estado !== "active") && (
            <p data-testid="comm-cred-revocadas" className="pt-2 text-[11px] text-slate-400">
              {creds.filter((k) => k.estado !== "active").length} credencial(es) revocada(s) en el historial.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
